import i18n from '@risksmart-app/i18n/src/i18n';
import type { GetControlsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import {
  useGetControlsRegister,
  type UseGetControlsRegisterArgs,
} from 'src/hooks/queries/control/useGetControlsRegister';
import { useGetRisksFlat } from 'src/hooks/queries/risk/useGetRisksFlat';
import { useLabelledFields as useControlLabelledFields } from 'src/pages/controls/useLabelledFields';

import { useCommonLookupLazy } from '@/hooks/useCommonLookupLazy';
import { getFriendlyId } from '@/utils/friendlyId';
import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import type { TableFields, TableRecord } from '@/utils/table/types';

import { UNRATED } from '../../gigawidget/types';
import { createDataSource } from '../createDataSource';
import { defaultClickthroughFilter } from '../dataSourceHelpers';
import {
  prefixedLabel,
  simpleField,
  useRelationshipTableProps,
} from './relationship-helpers';

export type RiskControlRow = TableRecord & {
  Id: string;
  RiskId: string;
  RiskFriendlyId: string;
  RiskTitle: string;
  RiskTier: string;
  RiskStatus: string;
  RiskTreatment: string;
  ControlId: string;
  ControlFriendlyId: string;
  ControlTitle: string;
  ControlType: string;
  DesignEffectiveness: string;
  PerformanceEffectiveness: string;
  OverallEffectiveness: string;
  TestScheduleStatus: string;
  TestScheduleStatusLabelled: string;
};

export default createDataSource<
  RiskControlRow,
  UseGetControlsRegisterArgs,
  GetControlsQuery
>({
  group: 'relationship',
  hasAccess: () => true,
  parentTypes: [Parent_Type_Enum.Risk, Parent_Type_Enum.Control],
  useWidgetQuery: useGetControlsRegister,
  useTablePropsHook: (
    data: GetControlsQuery | undefined,
    options: StatefulTableOptions<RiskControlRow>
  ) => {
    const fields = useMemo<TableFields<RiskControlRow>>(
      () => ({
        RiskFriendlyId: simpleField(
          prefixedLabel('risk_one', i18n.t('columns.id'))
        ),
        RiskTitle: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_name'))
        ),
        RiskTier: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_tier'))
        ),
        RiskStatus: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_status'))
        ),
        RiskTreatment: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_treatment'))
        ),
        ControlFriendlyId: simpleField(
          prefixedLabel('control_one', i18n.t('columns.id'))
        ),
        ControlTitle: simpleField(
          prefixedLabel('control_one', i18n.t('controls.columns.title'))
        ),
        ControlType: simpleField(
          prefixedLabel('control_one', i18n.t('controls.columns.type'))
        ),
        DesignEffectiveness: simpleField(
          prefixedLabel(
            'control_one',
            i18n.t('controls.columns.design_effectiveness')
          )
        ),
        PerformanceEffectiveness: simpleField(
          prefixedLabel(
            'control_one',
            i18n.t('controls.columns.performance_effectiveness')
          )
        ),
        OverallEffectiveness: simpleField(
          prefixedLabel('control_one', i18n.t('controls.columns.effectiveness'))
        ),
        TestScheduleStatusLabelled: simpleField(
          prefixedLabel(
            'control_one',
            i18n.t('controls.columns.testScheduleStatus')
          )
        ),
      }),
      []
    );

    const controlsLabelled = useControlLabelledFields(data?.control);
    const { getByValue: commonLookup } = useCommonLookupLazy();
    const { data: riskData, loading: risksLoading } = useGetRisksFlat({
      queryArgs: {},
    });

    const joinedData = useMemo<RiskControlRow[] | undefined>(() => {
      if (!controlsLabelled || !riskData?.risk) {
        return undefined;
      }

      const riskMap = new Map(riskData.risk.map((r) => [r.Id, r]));

      return controlsLabelled.flatMap((control) =>
        (control.parents ?? [])
          .filter(
            (p) => p.parent?.ObjectType === Parent_Type_Enum.Risk && p.parent.Id
          )
          .map((parent) => {
            const risk = riskMap.get(parent.parent!.Id);

            return {
              Id: `${parent.parent!.Id}_${control.Id}`,
              RiskId: parent.parent!.Id,
              RiskFriendlyId: risk?.SequentialId
                ? getFriendlyId(Parent_Type_Enum.Risk, risk.SequentialId)
                : '',
              RiskTitle: risk?.Title ?? parent.risk?.Title ?? '',
              RiskTier: commonLookup('tiers', risk?.Tier)?.label ?? '',
              RiskStatus: commonLookup('statuses', risk?.Status)?.label ?? '',
              RiskTreatment:
                commonLookup('treatments', risk?.Treatment)?.label ?? '',
              ControlId: control.Id,
              ControlFriendlyId: control.SequentialIdLabel ?? '',
              ControlTitle: control.Title ?? '',
              ControlType: control.Type ?? '',
              DesignEffectiveness: control.DesignEffectivenessLabelled ?? '',
              PerformanceEffectiveness:
                control.PerformanceEffectivenessLabelled ?? '',
              OverallEffectiveness: control.OverallEffectivenessLabelled ?? '',
              TestScheduleStatus: control.TestScheduleStatus ?? '',
              TestScheduleStatusLabelled:
                control.TestScheduleStatusLabelled ?? '',
            };
          })
      );
    }, [controlsLabelled, riskData?.risk, commonLookup]);

    const tableProps = useRelationshipTableProps(
      joinedData,
      fields,
      i18n.t('risk_control_other'),
      options
    );

    // The join stays undefined until the secondary risks query lands —
    // report that window as loading rather than "no data" (RSP-6539).
    return { ...tableProps, isEnrichmentLoading: risksLoading };
  },
  entityNamePlural: 'risk_control_other',
  entityNameSingular: 'risk_control_one',
  fields: 'riskControls.fields',
  dashboardFilterConfig: {},
  categoryGetters: [
    {
      id: 'riskFriendlyId',
      entityGroup: 'risk_one',
      name: () => prefixedLabel('risk_one', i18n.t('columns.id')),
      categoryGetter: (item: RiskControlRow) => item.RiskFriendlyId || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskFriendlyId'),
    },
    {
      id: 'riskTitle',
      entityGroup: 'risk_one',
      name: () => prefixedLabel('risk_one', i18n.t('risks.columns.risk_name')),
      categoryGetter: (item: RiskControlRow) => item.RiskTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTitle'),
    },
    {
      id: 'riskTier',
      entityGroup: 'risk_one',
      name: () => prefixedLabel('risk_one', i18n.t('risks.columns.risk_tier')),
      categoryGetter: (item: RiskControlRow) => item.RiskTier || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTier'),
    },
    {
      id: 'riskStatus',
      entityGroup: 'risk_one',
      name: () =>
        prefixedLabel('risk_one', i18n.t('risks.columns.risk_status')),
      categoryGetter: (item: RiskControlRow) => item.RiskStatus || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskStatus'),
    },
    {
      id: 'riskTreatment',
      entityGroup: 'risk_one',
      name: () =>
        prefixedLabel('risk_one', i18n.t('risks.columns.risk_treatment')),
      categoryGetter: (item: RiskControlRow) => item.RiskTreatment || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTreatment'),
    },
    {
      id: 'controlFriendlyId',
      entityGroup: 'control_one',
      name: () => prefixedLabel('control_one', i18n.t('columns.id')),
      categoryGetter: (item: RiskControlRow) =>
        item.ControlFriendlyId || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlFriendlyId'),
    },
    {
      id: 'controlTitle',
      entityGroup: 'control_one',
      name: () =>
        prefixedLabel('control_one', i18n.t('controls.columns.title')),
      categoryGetter: (item: RiskControlRow) => item.ControlTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlTitle'),
    },
    {
      id: 'controlType',
      entityGroup: 'control_one',
      name: () => prefixedLabel('control_one', i18n.t('controls.columns.type')),
      categoryGetter: (item: RiskControlRow) => item.ControlType || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlType'),
    },
    {
      id: 'designEffectiveness',
      entityGroup: 'control_one',
      name: () =>
        prefixedLabel(
          'control_one',
          i18n.t('controls.columns.design_effectiveness')
        ),
      categoryGetter: (item: RiskControlRow) =>
        item.DesignEffectiveness || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('DesignEffectiveness'),
      ratingColourKey: 'design_effectiveness',
    },
    {
      id: 'performanceEffectiveness',
      entityGroup: 'control_one',
      name: () =>
        prefixedLabel(
          'control_one',
          i18n.t('controls.columns.performance_effectiveness')
        ),
      categoryGetter: (item: RiskControlRow) =>
        item.PerformanceEffectiveness || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('PerformanceEffectiveness'),
      ratingColourKey: 'performance_effectiveness',
    },
    {
      id: 'overallEffectiveness',
      entityGroup: 'control_one',
      name: () =>
        prefixedLabel('control_one', i18n.t('controls.columns.effectiveness')),
      categoryGetter: (item: RiskControlRow) =>
        item.OverallEffectiveness || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('OverallEffectiveness'),
      ratingColourKey: 'effectiveness',
    },
    {
      id: 'testScheduleStatus',
      entityGroup: 'control_one',
      name: () =>
        prefixedLabel(
          'control_one',
          i18n.t('controls.columns.testScheduleStatus')
        ),
      categoryGetter: (item: RiskControlRow) =>
        item.TestScheduleStatus === '-'
          ? UNRATED
          : (item.TestScheduleStatusLabelled ?? UNRATED),
      clickthroughFilter: defaultClickthroughFilter(
        'TestScheduleStatusLabelled'
      ),
    },
  ],
});
